import type { ReactNode } from 'react';
import MetricCard from '../../../components/common/MetricCard';
import RadarChartCard from './RadarChartCard';
import type { RadarDataLeadership, RadarDatum, ChartSeries, SimpleMetric } from './model';
import Section from '@/components/common/Section';

export interface AssessmentRadarChartConfig {
    data: RadarDataLeadership[] | RadarDatum[];
    series?: ChartSeries[];
    footer?: ReactNode;
    title?: string;
    height?: number;
    maxDomain?: number;
    variant?: 'bare' | 'default';
}

interface AssessmentRadarSectionProps {
    title: string;
    subtitle?: string;
    iconKey?: string;
    accent?: 'purple' | 'blue' | 'green' | 'teal' | 'muted' | 'none';
    metrics: SimpleMetric[];
    minMetricWidth?: number;
    charts: AssessmentRadarChartConfig[];
    sectionClassName?: string;
}

const AssessmentRadarSection = ({
    title,
    subtitle,
    iconKey,
    accent = 'blue',
    metrics,
    minMetricWidth = 100,
    charts,
    sectionClassName = '',
}: AssessmentRadarSectionProps) => {
    const chartCount = Math.min(charts.length, 2);
    const gridClass = chartCount === 2 ? 'grid-cols-[300px_1fr_1fr]' : 'grid-cols-[300px_1fr]';

    const calculatedMaxValue = (maxDomain = 0): number => {
        // If maxDomain is 0 or undefined, try to calculate from data
        if (maxDomain <= 0 && charts.length > 0) {
            const firstChart = charts[0];
            if (firstChart.data && firstChart.data.length > 0) {
                // Find the maximum value in the data
                let maxValue = 0;
                firstChart.data.forEach(datum => {
                    if (typeof datum === 'object' && datum !== null) {
                        Object.values(datum).forEach(value => {
                            if (typeof value === 'number' && value > maxValue) {
                                maxValue = value;
                            }
                        });
                    }
                });
                return maxValue > 0 ? maxValue : 10; // Default to 10 if no data
            }
        }
        return maxDomain > 0 ? maxDomain : 10; // Default to 10 if maxDomain is 0
    };

    return (
        <div className="col-span-8">
            <Section accent={accent} className={sectionClassName}>
                <div className={`grid gap-6 ${gridClass}`}>
                    <Section title={title} accent={accent} iconKey={iconKey} showBorder={false}>
                        <div className="flex flex-col">
                            <p className="mt-1 text-base leading-snug text-gray-500">{subtitle}</p>
                            <div className="mt-auto pt-4">
                                <div className="flex flex-wrap gap-3">
                                    {metrics.map(m => (
                                        <MetricCard
                                            key={m.label}
                                            label={m.label}
                                            value={m.value}
                                            minWidth={minMetricWidth}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </Section>
                    {charts.slice(0, 2).map((c, idx) => (
                        <div key={idx} className="rounded-xl bg-white p-6">
                            <RadarChartCard
                                title={c.title || ''}
                                data={c.data}
                                series={c.series}
                                variant={c.variant || 'bare'}
                                height={c.height ?? 210}
                                footer={c.footer}
                                showTeam={idx > 0}
                                maxValueData={calculatedMaxValue(c.maxDomain ?? 0)}
                            />
                        </div>
                    ))}
                </div>
            </Section>
        </div>
    );
};

export default AssessmentRadarSection;
